// Device fingerprint collection utility for Vue.js frontend
class DeviceFingerprintCollector {
  constructor() {
    this.fingerprint = {};
  }

  // Collect all device fingerprint information
  async collect() {
    this.fingerprint.user_agent = navigator.userAgent;
    this.fingerprint.screen_resolution = `${screen.width}x${screen.height}`;
    this.fingerprint.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.fingerprint.language = navigator.language;
    this.fingerprint.platform = navigator.platform;
    this.fingerprint.plugins = this.getPlugins();
    this.fingerprint.canvas_fingerprint = this.getCanvasFingerprint();
    this.fingerprint.webgl_fingerprint = this.getWebGLFingerprint();
    this.fingerprint.local_storage = this.hasLocalStorage();
    this.fingerprint.session_storage = this.hasSessionStorage();
    this.fingerprint.indexed_db = this.hasIndexedDB();
    this.fingerprint.cookie_enabled = navigator.cookieEnabled;
    this.fingerprint.do_not_track = navigator.doNotTrack || 'unknown';

    return this.fingerprint;
  }

  // Get plugins list
  getPlugins() {
    const plugins = [];
    for (let i = 0; i < navigator.plugins.length; i++) {
      plugins.push(navigator.plugins[i].name);
    }
    return plugins.sort();
  }

  // Get Canvas fingerprint
  getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint test', 2, 2);
      return canvas.toDataURL().slice(-50);
    } catch (e) {
      return 'canvas_not_supported';
    }
  }

  // Get WebGL fingerprint
  getWebGLFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return 'webgl_not_supported';

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return 'debug_info_not_available';

      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      return `${vendor}~${renderer}`.slice(-50);
    } catch (e) {
      return 'webgl_error';
    }
  }

  // Check LocalStorage support
  hasLocalStorage() {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      return true;
    } catch (e) {
      return false;
    }
  }

  // Check SessionStorage support
  hasSessionStorage() {
    try {
      sessionStorage.setItem('test', 'test');
      sessionStorage.removeItem('test');
      return true;
    } catch (e) {
      return false;
    }
  }

  // Check IndexedDB support
  hasIndexedDB() {
    return !!window.indexedDB;
  }

  // Send fingerprint to server
  async sendToServer(endpoint = '/api/user/device-fingerprint/collect') {
    const fingerprint = await this.collect();

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fingerprint)
      });

      const result = await response.json();

      // If device fingerprint is successfully obtained, save to Cookie and localStorage
      if (result.code === 0 && result.data && result.data.device_fingerprint) {
        const deviceFingerprint = result.data.device_fingerprint;

        // Save to Cookie (30 days)
        document.cookie = `device_fingerprint=${deviceFingerprint}; expires=${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString()}; path=/`;

        // Save to localStorage
        localStorage.setItem('device_fingerprint', deviceFingerprint);
      }

      return result;
    } catch (error) {
      console.error('Failed to send fingerprint:', error);
      return null;
    }
  }

  // Get saved device fingerprint
  getSavedFingerprint() {
    // First try to get from Cookie
    const cookieMatch = document.cookie.match(/device_fingerprint=([^;]+)/);
    if (cookieMatch) {
      return cookieMatch[1];
    }

    // Get from localStorage
    return localStorage.getItem('device_fingerprint');
  }

  // Initialize device fingerprint
  async init() {
    const savedFingerprint = this.getSavedFingerprint();

    if (!savedFingerprint) {
      // If no saved fingerprint, collect and send to server
      await this.sendToServer();
    }
  }
}

// Create and export singleton instance
const deviceFingerprint = new DeviceFingerprintCollector();

export default deviceFingerprint;